import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference/actionTypes';
import { isMobileBrowser } from '../base/environment/utils';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import logger from './logger';
/**
 * A near-silent AudioContext used to keep the mobile browser audio session
 * in loudspeaker (media) mode while a conference is active.
 *
 * - Android Chrome: WebRTC's MODE_IN_COMMUNICATION defaults to earpiece.
 *   An active AudioContext output forces Chrome to stay in speaker mode.
 * - iOS Safari: AVAudioSession switches to voice-call category on WebRTC
 *   start, routing audio to earpiece. An active Web Audio output prevents
 *   this. However Safari suspends AudioContext in background, so we must
 *   resume it on visibility change.
 */
// Portable AudioContext constructor (handles Safari's webkitAudioContext).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AudioContextCtor = typeof AudioContext !== 'undefined'
    ? AudioContext
    // @ts-ignore -- webkitAudioContext exists on old Safari
    : typeof webkitAudioContext !== 'undefined' ? webkitAudioContext : undefined;
let _audioContext = null;
let _oscillator = null;
let _gainNode = null;
/** Whether a conference is currently active (guards visibility-resume logic). */
let _conferenceActive = false;
/**
 * Tries to resume a non-running AudioContext.
 *
 * Covers both 'suspended' (standard WebAudio) and 'interrupted' (iOS Safari
 * 16.4+ — set when a phone call interrupts the browser audio session).
 * If the context is already closed (e.g. after a failed conference), we
 * recreate it entirely.
 */
function _resumeIfSuspended() {
    if (!_audioContext) {
        return;
    }
    if (_audioContext.state === 'closed') {
        // Context was closed externally; rebuild the whole keep-alive.
        _stopSpeakerKeepAlive();
        _startSpeakerKeepAlive();
        return;
    }
    // 'suspended' = standard pause (tab hidden, autoplay policy).
    // 'interrupted' = iOS-specific state when a phone call takes over audio.
    // Both need resume(); 'running' and 'closed' do not.
    if (_audioContext.state !== 'running') {
        _audioContext.resume().catch(err => logger.warn('AudioContext resume failed', err));
    }
}
/**
 * Starts a nearly-silent oscillator through a new AudioContext.
 * This keeps the mobile browser audio session alive in speaker mode.
 *
 * On iOS Safari the AudioContext may start in 'suspended' state — we call
 * resume() immediately and also hook visibilitychange to re-resume whenever
 * the page comes back from background.
 *
 * @returns {void}
 */
function _startSpeakerKeepAlive() {
    if (_audioContext) {
        return;
    }
    if (!AudioContextCtor) {
        return;
    }
    try {
        _audioContext = new AudioContextCtor();
        _gainNode = _audioContext.createGain();
        _gainNode.gain.value = 0.001; // Nearly silent — keeps session alive without audible sound
        _oscillator = _audioContext.createOscillator();
        _oscillator.type = 'sine';
        _oscillator.frequency.value = 440;
        _oscillator.connect(_gainNode);
        _gainNode.connect(_audioContext.destination);
        _oscillator.start();
        // iOS Safari: AudioContext starts suspended until a user gesture.
        // resume() here handles the case where join was triggered by a gesture
        // that has already unlocked audio (e.g. pressing the Join button).
        if (_audioContext.state !== 'running') {
            _audioContext.resume().catch(err => logger.warn('AudioContext initial resume failed', err));
        }
        // Listen for state changes so we can react immediately when iOS
        // transitions to 'interrupted' (phone call) without waiting for
        // the next visibilitychange event.
        _audioContext.addEventListener('statechange', () => {
            if (_conferenceActive) {
                _resumeIfSuspended();
            }
        });
        logger.info('Speaker keep-alive started');
    }
    catch (err) {
        logger.warn('Failed to start speaker keep-alive', err);
    }
}
/**
 * Stops and cleans up the speaker keep-alive AudioContext.
 *
 * @returns {void}
 */
function _stopSpeakerKeepAlive() {
    try {
        _oscillator?.stop();
        _gainNode?.disconnect();
        _audioContext?.close();
    }
    catch (err) {
        logger.warn('Failed to stop speaker keep-alive', err);
    }
    finally {
        _oscillator = null;
        _gainNode = null;
        _audioContext = null;
    }
}
// iOS Safari suspends AudioContext when the page becomes hidden (user swipes
// to another app or locks the screen). We need to resume it when they come
// back so audio keeps routing to the loudspeaker.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (!_conferenceActive || document.hidden) {
            return;
        }
        _resumeIfSuspended();
    });
}
/**
 * Middleware that manages an AudioContext keepalive on mobile browsers
 * to prevent the audio from being routed to the earpiece during a conference.
 *
 * Applies to:
 *   - Android Chrome (Platform.OS === 'android')
 *   - iOS Safari   (Platform.OS === 'ios')
 */
MiddlewareRegistry.register(_store => next => action => {
    // Only activate on mobile browsers — native apps (React Native/iOS SDK)
    // have their own audio routing via AudioModeModule and don't need this.
    if (!isMobileBrowser()) {
        return next(action);
    }
    switch (action.type) {
        case CONFERENCE_JOINED:
            _conferenceActive = true;
            _startSpeakerKeepAlive();
            break;
        case CONFERENCE_FAILED:
        case CONFERENCE_LEFT:
            _conferenceActive = false;
            _stopSpeakerKeepAlive();
            break;
    }
    return next(action);
});
